import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "src/configuration";
import { useSignalR } from "src/hooks";
import {
  ActionItemDetailDto,
  ActionItemSummaryDto,
  EditActionItemDueDateDto,
  FetchError,
  optimisticallyUpdateQueryData,
  queryKeys,
  useAccessToken,
} from "src/http";

type Variables = {
  actionItemGuid: string;
  dueDate: string | null;
  planGuid: string;
};

const useEditActionItemDueDateRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editActionItemDueDateRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: EditActionItemDueDateDto = {
      dueDate: variables.dueDate,
    };

    const response = await fetch(`${configuration.apiRootUrl}/action-items/${variables.actionItemGuid}/due-date`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editActionItemDueDateRequest };
};

const useEditActionItemDueDate = () => {
  const { editActionItemDueDateRequest } = useEditActionItemDueDateRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: editActionItemDueDateRequest,
    onMutate: async (variables) => {
      let cachedSummaryData: ActionItemSummaryDto | undefined;
      let cachedDetailData: ActionItemDetailDto | undefined;

      cachedSummaryData = await optimisticallyUpdateQueryData<ActionItemSummaryDto>(
        queryClient,
        queryKeys.actionItems.getActionItemSummary(variables.actionItemGuid),
        () => ({
          dueDate: variables.dueDate,
        })
      );

      cachedDetailData = await optimisticallyUpdateQueryData<ActionItemDetailDto>(
        queryClient,
        queryKeys.actionItems.getActionItemDetail(variables.actionItemGuid),
        () => ({
          dueDate: variables.dueDate,
        })
      );

      return {
        cachedSummaryData,
        cachedDetailData,
      };
    },
    onSuccess: (_data, variables) => {
      signalR.sendRefetchActionItem(variables.actionItemGuid);
      signalR.sendRefetchComments(variables.actionItemGuid);
      signalR.sendRefetchPlan(variables.planGuid);
    },
    onError: (_error, variables, context) => {
      queryClient.setQueryData(
        queryKeys.actionItems.getActionItemSummary(variables.actionItemGuid),
        context?.cachedSummaryData
      );

      queryClient.setQueryData(
        queryKeys.actionItems.getActionItemDetail(variables.actionItemGuid),
        context?.cachedDetailData
      );
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.actionItems.getActionItemSummary(variables.actionItemGuid));
      queryClient.invalidateQueries(queryKeys.actionItems.getActionItemDetail(variables.actionItemGuid));
      queryClient.invalidateQueries(queryKeys.actionItems.getActionItemFeed(variables.actionItemGuid));
      queryClient.invalidateQueries(queryKeys.plans.getPlan(variables.planGuid));
      queryClient.invalidateQueries(queryKeys.plans.getPlanSubitems(variables.planGuid));
    },
  });
};

export { useEditActionItemDueDate };
